import { Add, Close, FilterAltOutlined } from '@mui/icons-material';
import { Box, Button, IconButton, MenuItem, TextField, Tooltip } from '@mui/material';
import { FilterContext, useFilter } from '@synergy/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { PaloModal, paloActiveFilters } from '@synergy/components-palo';
import { PaloThemeProvider, tokens } from '@synergy/theme-palo';
import { PropsWithChildren, ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { findFilterValueTranslationId, noop } from '@synergy/utils';
import { useSearchParams } from 'react-router-dom';

import {
    PaloFilterRow,
    paloAddressFilterCount,
    paloClearFilters,
    paloOperatorTakesTwo,
    paloOperatorTakesValue,
    paloReadRows,
    paloWriteRows,
} from './palo-filter-rows';
import {
    PaloListFilterField,
    PaloListFilterOperator,
    paloDefaultOperator,
    usePaloListFilters,
} from './use-palo-list-filters';
import { PaloListKeyProvider } from './palo-list-key';

/** What each comparison is called, in the words of the list screens. */
const OPERATOR_LABEL: Record<PaloListFilterOperator, string> = {
    eq: 'ist',
    neq: 'ist nicht',
    contains: 'enthält',
    starts: 'beginnt mit',
    in: 'ist eine von',
    from: 'ab',
    until: 'bis',
    gt: 'größer als',
    lt: 'kleiner als',
    between: 'zwischen',
    empty: 'ist leer',
    notEmpty: 'ist gefüllt',
};

/**
 * What a field is called.
 *
 * The list declares which fields it can answer, not what they are called —
 * the wording lives where every other label lives. A field without one shows
 * its own name rather than nothing, so a newly declared field is usable the
 * day it is declared and only reads roughly until someone names it.
 */
const useFieldLabel = (): ((key: string) => string) => {
    const intl = useIntl();

    return (key: string): string => {
        const id = `app.label.${key}`;

        return intl.messages[id] ? intl.formatMessage({ id }) : key;
    };
};

const valueLabel = (value: string): ReactNode => {
    const id = findFilterValueTranslationId(value);

    return id ? <FormattedMessage id={id} /> : value;
};

interface PaloFilterPanelProps {
    /** The list as the server names it, e.g. `invoices`. */
    listKey: string;
    title?: ReactNode;
}

/**
 * The filter of a list: one row per condition, each naming its field, its
 * comparison and its value.
 *
 * What it offers comes from the list itself, so a field becomes filterable by
 * being declared once on the server rather than by being wired up here as well
 * (#2209). The hand-written filters a screen passes as children keep their
 * place below — those are the ones that look up a record by name and hand over
 * its id, which no generic control can do.
 */
export const PaloFilterPanel = ({
    listKey,
    title,
    children,
}: PropsWithChildren<PaloFilterPanelProps>): ReactElement => {
    const declared = usePaloListFilters(listKey);
    // A reference is an id, and a row offers nothing but a box to type it in.
    // Those fields belong to the look-up filters below, which turn a name into
    // the id — one field, one place to set it (#2231 R-119).
    const fields = useMemo(() => declared.filter(field => 'reference' !== field.type), [declared]);
    const fieldLabel = useFieldLabel();
    const [searchParams, setSearchParams] = useSearchParams();
    const { filter, reset, updateFilter, ...parentFilterContext } = useFilter();
    const [isOpen, setIsOpen] = useState(false);
    const [rows, setRows] = useState<PaloFilterRow[]>([]);
    const [nextFilter, setNextFilter] = useState<URLSearchParams>(new URLSearchParams());

    // The dialog stages its changes: what is typed in it reaches the list when
    // it is applied, not while it is being filled in.
    useEffect(() => {
        if (isOpen) {
            setRows(paloReadRows(searchParams, fields));
            setNextFilter(new URLSearchParams(filter));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const activeCount = paloAddressFilterCount(searchParams);

    const fieldOf = (key: string): PaloListFilterField | undefined => fields.find(entry => entry.key === key);

    const setRow = (index: number, change: Partial<PaloFilterRow>): void =>
        setRows(current => current.map((row, at) => (at === index ? { ...row, ...change } : row)));

    const addRow = (): void => {
        const field = fields.find(entry => !rows.some(row => row.key === entry.key)) ?? fields[0];

        if (field) {
            setRows(current => [...current, { key: field.key, operator: paloDefaultOperator(field), values: [''] }]);
        }
    };

    const onCommit = (): void => {
        // Both halves write into one address: the rows above, and the
        // hand-written filters below, which still speak in plain names and
        // are written back the way they were before, as `filter[name]`.
        const next = paloWriteRows(searchParams, fields, rows);

        // What the sub-views of the navigation set is sent repeated, under a
        // name of its own, and belongs to neither half — it stays untouched,
        // or opening the filter would empty the sub-view that led there.
        const repeated = new Set(
            Array.from(searchParams.keys())
                .filter(key => key.endsWith('][]'))
                .map(key => key.substring(7, key.indexOf(']', 7)))
        );
        const mine = (key: string): boolean => !fieldOf(key) && !repeated.has(key);

        paloActiveFilters(filter)
            .filter(([key]: [string, string]) => mine(key))
            .forEach(([key]: [string, string]) => next.delete(`filter[${key}]`));
        paloActiveFilters(nextFilter)
            .filter(([key]: [string, string]) => mine(key))
            .forEach(([key, value]: [string, string]) => next.append(`filter[${key}]`, value));

        setSearchParams(next);
        setIsOpen(false);
    };

    const onReset = (): void => {
        setRows([]);

        // A list that came with a filter dialog has a context that knows what
        // its own empty state is — a default sub-view, a page size to keep.
        // A list that never had one has none, and then emptying the address
        // is what resetting means.
        if (noop === reset) {
            setSearchParams(paloClearFilters(searchParams));
        } else {
            reset();
        }

        setIsOpen(false);
    };

    const actionChildren = [
        <Button key="1" variant="text" color="inherit" onClick={onReset} sx={{ color: tokens.color.ink2 }}>
            <FormattedMessage id="app.common.reset" />
        </Button>,
        <Button key="2" variant="contained" color="primary" onClick={onCommit}>
            <FormattedMessage id="app.common.filter" />
        </Button>,
    ];

    const valueField = (row: PaloFilterRow, index: number, at: 0 | 1): ReactElement => {
        const field = fieldOf(row.key);
        const choices = field?.choices ?? [];

        return (
            <TextField
                size="small"
                select={choices.length > 0}
                type={'date' === field?.type ? 'date' : 'text'}
                value={row.values[at] ?? ''}
                onChange={event => {
                    const values = [...row.values];
                    values[at] = event.target.value;
                    setRow(index, { values });
                }}
                sx={{ flex: 1, minWidth: 0 }}
            >
                {choices.map(choice => (
                    <MenuItem key={choice} value={choice}>
                        {valueLabel(choice)}
                    </MenuItem>
                ))}
            </TextField>
        );
    };

    return (
        <>
            <PaloThemeProvider>
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <Button
                        variant="text"
                        size="small"
                        startIcon={<FilterAltOutlined />}
                        onClick={() => setIsOpen(true)}
                        sx={{ color: activeCount > 0 ? tokens.color.accent : tokens.color.ink2 }}
                    >
                        <FormattedMessage id="app.common.filter" />
                        {activeCount > 0 && (
                            <Box
                                component="span"
                                sx={{
                                    ml: '6px',
                                    minWidth: 17,
                                    height: 17,
                                    px: '4px',
                                    borderRadius: '9px',
                                    backgroundColor: tokens.color.accent,
                                    color: '#fff',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    lineHeight: '17px',
                                    textAlign: 'center',
                                }}
                            >
                                {activeCount}
                            </Box>
                        )}
                    </Button>
                </Box>
            </PaloThemeProvider>
            <PaloModal
                isVisible={isOpen}
                title={title ?? <FormattedMessage id="app.filter.filterTitle" />}
                handleClose={() => setIsOpen(false)}
                actionChildren={actionChildren}
                themeContent
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mb: children ? '16px' : 0 }}>
                    {rows.map((row, index) => {
                        const field = fieldOf(row.key);

                        return (
                            <Box
                                key={`${row.key}-${row.operator}-${index}`}
                                sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                            >
                                <TextField
                                    size="small"
                                    select
                                    value={row.key}
                                    onChange={event => {
                                        const chosen = fieldOf(event.target.value);
                                        setRow(index, {
                                            key: event.target.value,
                                            operator: chosen ? paloDefaultOperator(chosen) : row.operator,
                                            values: [''],
                                        });
                                    }}
                                    sx={{ flex: 1, minWidth: 0 }}
                                >
                                    {fields.map(entry => (
                                        <MenuItem key={entry.key} value={entry.key}>
                                            {fieldLabel(entry.key)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    size="small"
                                    select
                                    value={row.operator}
                                    onChange={event =>
                                        setRow(index, { operator: event.target.value as PaloListFilterOperator })
                                    }
                                    sx={{ width: 140, flexShrink: 0 }}
                                >
                                    {(field?.operators ?? []).map(operator => (
                                        <MenuItem key={operator} value={operator}>
                                            {OPERATOR_LABEL[operator]}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                {paloOperatorTakesValue(row.operator) && valueField(row, index, 0)}
                                {paloOperatorTakesTwo(row.operator) && valueField(row, index, 1)}
                                <Tooltip title="Zeile entfernen">
                                    <IconButton
                                        size="small"
                                        onClick={() => setRows(current => current.filter((_, at) => at !== index))}
                                        sx={{ color: tokens.color.ink3 }}
                                    >
                                        <Close fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        );
                    })}
                    {fields.length > 0 && (
                        <Box>
                            <Button
                                size="small"
                                startIcon={<Add />}
                                onClick={addRow}
                                sx={{ color: tokens.color.accent }}
                            >
                                Filter hinzufügen
                            </Button>
                        </Box>
                    )}
                </Box>
                <FilterContext.Provider
                    value={{
                        ...parentFilterContext,
                        filter: nextFilter,
                        reset: noop,
                        updateFilter: updated => setNextFilter(new URLSearchParams(updated)),
                    }}
                >
                    <PaloListKeyProvider listKey={listKey}>{children}</PaloListKeyProvider>
                </FilterContext.Provider>
            </PaloModal>
        </>
    );
};
